// ───────────────────────────────────────────────────
// src/features/stock/components/ProductActionCell.tsx
// ───────────────────────────────────────────────────
import React from 'react';
import { UseMutationResult } from '@tanstack/react-query';

import type { Product} from '../../stock/api/productApi'
import {
  OpenBalancePayload,
  OpenBalanceResult,
  RecordStockMovementPayload,
  RecordStockMovementResult,
} from '../../stock/api/stockApi';
import { UpdateProductPayload, DeleteProductPayload } from '../api/productApi';

type Mutation<TData, TVars> = UseMutationResult<TData, Error, TVars, unknown>;

interface ActionCellProps {
  row: Product & { __isNew?: boolean };
  editingStockId: number | null;
  setEditingStockId: (id: number | null) => void;
  stockQty: number;
  setStockQty: (qty: number) => void;
  currentQty: number | null;
  openBalM: Mutation<OpenBalanceResult, OpenBalancePayload>;
  recordM: Mutation<RecordStockMovementResult, RecordStockMovementPayload>;
  updateM: Mutation<any, UpdateProductPayload>;
  deleteM: Mutation<any, DeleteProductPayload>;
}

export default function ProductActionCell({
  row,
  editingStockId,
  setEditingStockId,
  stockQty,
  setStockQty,
  currentQty,
  openBalM,
  recordM,
  updateM,
  deleteM,
}: ActionCellProps) {
  const pid = row.id_product;
  const isRestock = currentQty !== null;
  const minQty = isRestock ? -currentQty! : 0;

  // ─── EDITING MODE (open‐balance or restock) ───────────────────────────
  if (editingStockId === pid) {
    // use "pending" because your mutations expose status: 'idle'|'pending'|'success'|'error'
    const isPending = isRestock
      ? recordM.status === 'pending'
      : openBalM.status === 'pending';

    return (
      <div className="flex space-x-2">
        {/* Only show the number input when restocking an existing product */}
        {isRestock && (
          <input
            type="number"
            min={minQty}
            value={stockQty}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= minQty) setStockQty(v);
            }}
            onClick={(e) => e.stopPropagation()}
            className="bg-neutral-700 text-neutral-100 px-2 py-1 rounded w-20 appearance-none no-spinner"
          />
        )}

        {/* Accept (new‐balance) / Restock */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isRestock) {
              recordM.mutate({
                items: [
                  {
                    id_product: pid,
                    qty: stockQty,        // use `qty` per your TS type
                    id_location: 0,
                  },
                ],
              });
            } else {
              openBalM.mutate({
                id_product: pid,
                qty: stockQty,
                id_location: 0,
              });
            }
          }}
          className="text-emerald-400 hover:text-emerald-300"
          disabled={isPending}
        >
          {isPending
            ? isRestock
              ? 'Restocking…'
              : 'Opening…'
            : isRestock
            ? 'Restock'
            : 'Accept'}
        </button>

        {/* Cancel */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingStockId(null);
          }}
          className="text-rose-400 hover:text-rose-300"
          disabled={isPending}
        >
          Cancel
        </button>
      </div>
    );
  }

  // ─── NORMAL MODE (restock button + modify/delete) ─────────────────────
  return (
    <div className="flex space-x-2">
      {isRestock && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingStockId(pid);
            setStockQty(0);
          }}
          className="text-blue-400 hover:text-blue-300"
        >
          Restock
        </button>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          updateM.mutate({
            id: pid,
            product_sku: row.product_sku ?? undefined,
            product_name: row.product_name,
            brand_name: row.brand_name,
            product_price_purchase: row.product_price_purchase,
            product_price_sales: row.product_price_sales,
          });
        }}
        className="text-amber-400 hover:text-amber-300"
        disabled={updateM.status === 'pending'}
      >
        {updateM.status === 'pending' ? 'Saving…' : 'Modify'}
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          deleteM.mutate({ id: pid });
        }}
        className="text-rose-400 hover:text-rose-300"
        disabled={deleteM.status === 'pending'}
      >
        {deleteM.status === 'pending' ? 'Deleting…' : 'Delete'}
      </button>
    </div>
  );
}